import { useCallback, useEffect, useMemo, useState } from "react";
import { Chip, IconButton, Menu, MenuItem } from "@mui/material";
import {
  Add as AddIcon,
  Delete as DeleteIcon,
  MoreVert,
} from "@mui/icons-material";
import { useSnackbar } from "notistack";
import DataTable from "@accredo/fe-shared/components/DataTable.jsx";
import {
  deleteMappingProfile,
  fetchConnectors,
  fetchMappingProfiles,
} from "@accredo/fe-shared/services/importMappingProfileService.js";
import ImportMappingProfileFormModal from "../ImportMappingProfileFormModal.jsx";

const RECORD_TYPE_LABELS = {
  gesellschaft: "Gesellschaft",
  kunde: "Kunde",
  vertrag: "Vertrag",
  objekt: "Objekt",
  objektsparte: "Objektsparte",
  user: "Benutzer",
  bank_account: "Bankkonto",
  user_contract_assignments: "Benutzerzuweisungen",
};

export default function ImportMappingProfilesTable() {
  const { enqueueSnackbar } = useSnackbar();
  const [profiles, setProfiles] = useState([]);
  const [connectors, setConnectors] = useState([]);
  const [loading, setLoading] = useState(true);
  const [hasLoaded, setHasLoaded] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [rowMenuState, setRowMenuState] = useState({
    anchorEl: null,
    menuRowId: null,
  });

  useEffect(() => {
    fetchConnectors()
      .then((res) => setConnectors(res.data ?? []))
      .catch(() =>
        enqueueSnackbar("Connectoren konnten nicht geladen werden", {
          variant: "error",
        })
      );
  }, [enqueueSnackbar]);

  const load = useCallback(() => {
    setLoading(true);
    fetchMappingProfiles()
      .then((res) => {
        setProfiles(res.data ?? []);
        setHasLoaded(true);
      })
      .catch(() =>
        enqueueSnackbar("Profile konnten nicht geladen werden", {
          variant: "error",
        })
      )
      .finally(() => setLoading(false));
  }, [enqueueSnackbar]);

  useEffect(() => {
    load();
  }, [load]);

  function openCreate() {
    setEditingId(null);
    setModalOpen(true);
  }
  function openEdit(row) {
    setEditingId(row.id);
    setModalOpen(true);
  }
  function handleModalClose(saved) {
    setModalOpen(false);
    if (saved) load();
  }

  function handleMenuOpen(e, id) {
    e.stopPropagation();
    setRowMenuState({ anchorEl: e.currentTarget, menuRowId: id });
  }
  function handleMenuClose(e) {
    e?.stopPropagation();
    setRowMenuState({ anchorEl: null, menuRowId: null });
  }

  async function handleDelete(e, profile) {
    handleMenuClose(e);
    if (!window.confirm("Mapping-Profil wirklich löschen?")) return;
    try {
      await deleteMappingProfile(profile.id);
      enqueueSnackbar("Profil gelöscht", { variant: "success" });
      load();
    } catch {
      enqueueSnackbar("Löschen fehlgeschlagen", { variant: "error" });
    }
  }

  const columns = useMemo(
    () => [
      {
        accessorKey: "import_connector_id",
        header: "Connector",
        filterVariant: "select",
        filterSelectOptions: connectors.map((c) => ({
          value: c.id,
          label: c.name ?? c.slug,
        })),
        Cell: ({ row }) => {
          const c = connectors.find(
            (c) => c.id === row.original.import_connector_id
          );
          return c?.name ?? c?.slug ?? row.original.import_connector_id;
        },
      },
      {
        accessorKey: "record_type",
        header: "Entitätstyp",
        filterVariant: "select",
        filterSelectOptions: Object.entries(RECORD_TYPE_LABELS).map(
          ([value, label]) => ({ value, label })
        ),
        Cell: ({ row, cell }) =>
          row?.original?.record_type_label ??
          RECORD_TYPE_LABELS[cell.getValue()] ??
          cell.getValue(),
      },
      {
        accessorKey: "match_key",
        header: "Match-Key",
      },
      {
        accessorKey: "write_mode",
        header: "Strategie",
      },
      {
        accessorKey: "is_active",
        header: "Aktiv",
        size: 90,
        Cell: ({ cell }) => (
          <Chip
            size="small"
            label={cell.getValue() ? "Aktiv" : "Inaktiv"}
            color={cell.getValue() ? "success" : "default"}
          />
        ),
      },
      {
        id: "actions",
        header: "",
        size: 56,
        enableSorting: false,
        enableColumnFilter: false,
        enableColumnHiding: false,
        Cell: ({ row }) => (
          <>
            <IconButton
              size="small"
              onClick={(e) => handleMenuOpen(e, row.original.id)}
            >
              <MoreVert fontSize="small" />
            </IconButton>
            <Menu
              anchorEl={rowMenuState.anchorEl}
              open={
                Boolean(rowMenuState.anchorEl) &&
                rowMenuState.menuRowId === row.original.id
              }
              onClose={handleMenuClose}
            >
              <MenuItem onClick={(e) => handleDelete(e, row.original)}>
                <DeleteIcon fontSize="small" sx={{ mr: 1 }} />
                Löschen
              </MenuItem>
            </Menu>
          </>
        ),
      },
    ],
    [connectors, rowMenuState]
  );

  return (
    <>
      <DataTable
        columns={columns}
        data={profiles}
        isLoading={loading}
        hasLoaded={hasLoaded}
        manualSorting={false}
        manualFiltering={false}
        enableGlobalFilter={true}
        enableColumnFilters={true}
        initialState={{ showColumnFilters: true }}
        onRowClick={openEdit}
        toolbarActions={[
          {
            type: "button",
            label: "Neu",
            icon: <AddIcon />,
            onClick: openCreate,
            variant: "primary",
          },
        ]}
      />

      <ImportMappingProfileFormModal
        open={modalOpen}
        onClose={handleModalClose}
        profileId={editingId}
      />
    </>
  );
}
